import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Phone, MapPin, Clock, Send, Printer, Building2 } from 'lucide-react';

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };
const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
const slideLeft = { hidden: { opacity: 0, x: -50 }, visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
const slideRight = { hidden: { opacity: 0, x: 50 }, visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };

function AnimSection({ children, className = '', variants = stagger }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.12 });
  return (
    <motion.section ref={ref} variants={variants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className={className}>
      {children}
    </motion.section>
  );
}

const locations = [
  { name: 'Maryland (HQ)', address: '1301 York Rd Ste 800 #1205', city: 'Lutherville, MD 21093', phone: '(410) 505-8875' },
  { name: 'Maryland', address: '33 W Franklin St Ste 100', city: 'Hagerstown, MD 21740', phone: '(410) 505-8875' },
  { name: 'Washington, DC', address: '1763 Columbia Rd NW #1007', city: 'Washington, DC 20009', phone: '(202) 314-2806' },
  { name: 'Minnesota', address: '7900 International Drive, Suite 300 #7022', city: 'Bloomington, MN 55425', phone: '(952) 213-4244' },
  { name: 'Virginia', address: '950 N Washington St 3rd Flr #2005', city: 'Alexandria, VA 22314', phone: '(703) 337-1772' },
];

export default function ContactUs() {
  const { apiClient } = useAuth();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post('/api/contact', formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error('Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16 overflow-hidden" data-testid="contact-us-page">
      {/* ═══════════ HERO — Visual first ═══════════ */}
      <section className="relative h-[50vh] flex items-end">
        <motion.img
          src="https://images.unsplash.com/photo-1746021451691-4385f318ec13?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Modern clinic interior"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.06 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--text-headline)] via-[var(--text-headline)]/30 to-transparent" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative z-10 max-w-7xl mx-auto px-6 pb-12 w-full"
        >
          <p className="uppercase tracking-[0.3em] text-xs font-bold text-[var(--primary-green)] mb-3">Get in Touch</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white">
            We'd Love to <span className="text-[var(--primary-green)]">Hear From You</span>
          </h1>
        </motion.div>
      </section>

      {/* ═══════════ QUICK CONTACT INFO BAR ═══════════ */}
      <AnimSection className="bg-[var(--primary-blue)] text-white py-6 px-6" data-testid="contact-bar">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-8 md:gap-16">
          <motion.div variants={fadeUp} className="flex items-center gap-3">
            <Phone size={18} className="text-[var(--primary-green)]" />
            <a href="tel:4105058875" className="font-semibold text-sm hover:text-[var(--primary-green)] transition-colors">(410) 505-8875</a>
          </motion.div>
          <motion.div variants={fadeUp} className="flex items-center gap-3">
            <Printer size={18} className="text-[var(--primary-green)]" />
            <span className="text-sm">Fax: (410) 988-3619</span>
          </motion.div>
          <motion.div variants={fadeUp} className="flex items-center gap-3">
            <Mail size={18} className="text-[var(--primary-green)]" />
            <a href="mailto:admin@givinghopes.com" className="font-semibold text-sm hover:text-[var(--primary-green)] transition-colors">admin@givinghopes.com</a>
          </motion.div>
        </div>
      </AnimSection>

      {/* ═══════════ FORM + SIDEBAR ═══════════ */}
      <AnimSection className="section-padding" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-12">
          {/* Sidebar — Info */}
          <motion.div variants={slideLeft} className="lg:col-span-2 space-y-6">
            <div className="card-elevated p-6">
              <h3 className="text-lg font-heading font-bold mb-4">General Contact</h3>
              <div className="space-y-4">
                <div className="flex gap-3 items-start">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Phone size={16} className="text-[var(--primary-blue)]" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">(410) 505-8875</p>
                    <p className="text-xs text-[var(--text-body)]">Call or Text</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Mail size={16} className="text-[var(--primary-blue)]" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">admin@givinghopes.com</p>
                    <p className="text-xs text-[var(--text-body)]">We respond within 24 hours</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Printer size={16} className="text-[var(--primary-blue)]" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">(410) 988-3619</p>
                    <p className="text-xs text-[var(--text-body)]">Fax</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-elevated p-6">
              <h3 className="text-lg font-heading font-bold mb-4">Book Online</h3>
              <p className="text-sm text-[var(--text-body)] mb-4">Schedule appointments through our booking partner:</p>
              <a
                href="https://headway.co/providers/sweetie-doychinov?utm_source=pem&utm_medium=direct_link&utm_campaign=75900"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-block text-sm !px-6 !py-2.5"
                data-testid="book-online-link"
              >
                Book on Headway
              </a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div variants={slideRight} className="lg:col-span-3">
            <div className="card-elevated p-8">
              <h2 className="text-2xl font-heading font-bold mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Full Name *</label>
                    <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} required placeholder="John Doe"
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-alt)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)] text-sm" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Email *</label>
                    <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-alt)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)] text-sm" data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Phone</label>
                    <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="(555) 000-0000"
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-alt)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)] text-sm" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Subject *</label>
                    <select id="subject" name="subject" value={formData.subject} onChange={handleChange} required
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-alt)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)] text-sm" data-testid="contact-subject-select">
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="evaluation">Evaluation Information</option>
                      <option value="therapy">Therapy Services</option>
                      <option value="training">Training Programs</option>
                      <option value="insurance">Insurance Questions</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold mb-1.5 text-[var(--text-headline)]">Message *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows="5" placeholder="Tell us how we can help..."
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-alt)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)] text-sm resize-none" data-testid="contact-message-input" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit-btn">
                  {loading ? 'Sending...' : <><Send size={16} /> Send Message</>}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </AnimSection>

      {/* ═══════════ LOCATIONS ═══════════ */}
      <AnimSection className="section-padding bg-[var(--bg-alt)]" data-testid="locations-section">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-[var(--primary-blue)] mb-3">Our Locations</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold">Find Us Near You</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((loc, i) => (
              <motion.div key={loc.name + loc.city} variants={fadeUp} className="card-elevated p-6 group hover:border-[var(--primary-blue)]/30 transition-colors" data-testid={`location-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-[var(--primary-blue)] transition-colors">
                    <Building2 size={18} className="text-[var(--primary-blue)] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading font-bold text-sm">{loc.name}</h3>
                </div>
                <div className="space-y-2 text-sm text-[var(--text-body)]">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-[var(--primary-blue)] mt-0.5 flex-shrink-0" />
                    <span>{loc.address}<br />{loc.city}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-[var(--primary-blue)] flex-shrink-0" />
                    <a href={`tel:${loc.phone.replace(/[^0-9]/g, '')}`} className="hover:text-[var(--primary-blue)] transition-colors font-semibold">{loc.phone}</a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimSection>

      {/* ═══════════ INSURANCES ═══════════ */}
      <AnimSection className="py-16 px-6" data-testid="insurance-section">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div variants={fadeUp}>
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-[var(--primary-blue)] mb-3">Accepted Insurance</p>
            <h2 className="text-2xl font-heading font-bold mb-8">Insurances We Accept</h2>
          </motion.div>
          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6 text-sm text-[var(--text-body)]">
            {['Medicare', 'Aetna', 'Cigna', 'Tricare East', 'BCBS CareFirst', 'Medicaid', 'United HealthCare', 'BlueCross BlueShield', 'Priority Partners'].map((ins) => (
              <span key={ins} className="px-5 py-2.5 rounded-xl bg-[var(--bg-alt)] border border-[var(--border-color)] font-semibold text-xs">
                {ins}
              </span>
            ))}
          </motion.div>
        </div>
      </AnimSection>
    </div>
  );
}
